import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Card,
  CardHeader,
  CardContent,
  IconButton
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { heIL } from '@mui/x-data-grid/locales';

import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ResponsiveToolbar from '../components/ResponsiveToolbar';
import ResponsiveDataView from '../components/ResponsiveDataView';
import MobileDialog from './dialogs/MobileDialog';

// 1) Import MUI Theme utilities
import { createTheme, ThemeProvider } from '@mui/material/styles';

// 2) Create a custom theme with a slightly bigger base font
const theme = createTheme({
  direction: 'rtl', // if you need RTL
  typography: {
    fontSize: 16, // Default is 14, bump to 16 for a bit bigger text
  },
});

const GroupsManagementPage = () => {
  const { userProfile } = useUser();
  
  // Get UI configuration from user profile
  const uiConfig = userProfile?.ui_config || {};
  const role = userProfile?.role;
  const includeGroups = uiConfig.include_groups !== false; // Default to true if not specified
  const allowedGroupTypes = uiConfig.group_types || ['Flow', 'Year', 'General']; // Default if not specified
  const groupsSchemas = uiConfig.groups_schemas || {};
  
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupTypeFilter, setGroupTypeFilter] = useState('All');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [newGroup, setNewGroup] = useState({
    group_name: '',
    description: '',
    group_type: allowedGroupTypes.length > 0 ? allowedGroupTypes[0] : '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: '',
  });

  // New state for deletion password protection phase
  const [deleteDialogStep, setDeleteDialogStep] = useState('confirm');
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordVisible, setDeletePasswordVisible] = useState(false);
  const [deleteFinalLoading, setDeleteFinalLoading] = useState(false);
  const [deleteFinalError, setDeleteFinalError] = useState('');
  const [deleteFinalSuccess, setDeleteFinalSuccess] = useState('');

  const navigate = useNavigate();

  // Helper function to get group type label from schema
  const getGroupTypeLabel = (groupType) => {
    return groupsSchemas[groupType]?.label || 
           (groupType === 'Flow' ? 'שוטף' : 
            groupType === 'Year' ? 'דו"ח שנתי' : 
            groupType === 'General' ? 'כללי' : 
            groupType);
  };

  useEffect(() => {
    if (!includeGroups) {
      navigate('/');
      return;
    }
    
    const fetchGroups = async () => {
      setLoading(true);
      try {
        const response = await API.get('/groups/');
        setGroups(response.data);
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [includeGroups, navigate]);

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: '', severity: '' });
  };

  const validateInputs = () => {
    if (!newGroup.group_name.trim()) {
      setSnackbar({
        open: true,
        message: 'שם הקבוצה הוא שדה חובה',
        severity: 'error',
      });
      return false;
    }
    return true;
  };

  const handleCreateGroup = async () => {
    if (!validateInputs()) return;

    try {
      const endpoint = '/group/create/';
      const response = await API.post(endpoint, {
        group_name: newGroup.group_name,
        group_type: newGroup.group_type,
        description: newGroup.description,
      });

      const createdGroup = {
        GroupID: response.data.group_id,
        GroupName: response.data.group_name,
        GroupType: response.data.group_type,
        Description: newGroup.description,
      };

      setGroups((prev) => [...prev, createdGroup]);
      setSnackbar({
        open: true,
        message: 'הקבוצה נוצרה בהצלחה',
        severity: 'success',
      });

      setCreateDialogOpen(false);
      setNewGroup({ 
        group_name: '', 
        description: '', 
        group_type: allowedGroupTypes.length > 0 ? allowedGroupTypes[0] : '' 
      });

      navigate(`/group/${createdGroup.GroupID}`);
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setSnackbar({
              open: true,
              message: error.response.data.error
                ? `שגיאה: ${error.response.data.error}`
                : 'שגיאה: שם הקבוצה כבר קיים בקבוצה שלך.',
              severity: 'error',
            });
            break;
          case 500:
            setSnackbar({
              open: true,
              message: 'שגיאה פנימית בשרת. נסה שוב מאוחר יותר.',
              severity: 'error',
            });
            break;
          default:
            setSnackbar({
              open: true,
              message: 'אירעה שגיאה לא ידועה.',
              severity: 'error',
            });
        }
      } else {
        setSnackbar({
          open: true,
          message: 'שגיאה בחיבור לשרת.',
          severity: 'error',
        });
      }
    }
  };

  const confirmDeleteGroup = (group) => {
    setGroupToDelete(group);
    setDeleteDialogStep('confirm');
    setDeletePassword('');
    setDeletePasswordVisible(false);
    setDeleteFinalError('');
    setDeleteFinalSuccess('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;
    setDeleteFinalLoading(true);
    setDeleteFinalError('');
    try {
      await API.post('/group/delete/', { group_id: groupToDelete.GroupID, password: deletePassword });
      setGroups((prev) =>
        prev.filter((g) => g.GroupID !== groupToDelete.GroupID)
      );
      setDeleteFinalSuccess('הקבוצה נמחקה בהצלחה!');
      // Auto-close the dialog after showing success message
      setTimeout(() => {
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
        setDeleteDialogStep('confirm');
        setDeletePassword('');
        setDeletePasswordVisible(false);
        setDeleteFinalSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error deleting group:', error);
      if (error.response && error.response.status === 401) {
        setDeleteFinalError('סיסמה שגויה');
      } else {
        setDeleteFinalError('אירעה שגיאה במחיקת הקבוצה.');
      }
    } finally {
      setDeleteFinalLoading(false);
    }
  };

  const filteredGroups = groups.filter((group) => {
    const matchesSearch = (group.GroupName?.toLowerCase() || '').includes(
      searchTerm.toLowerCase()
    );
    const matchesType =
      groupTypeFilter === 'All' || group.GroupType === groupTypeFilter;
    return matchesSearch && matchesType;
  });

  const columns = [
    {
      field: 'GroupName',
      headerName: 'שם הקבוצה',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Button
          variant="text"
          color="primary"
          onClick={() => navigate(`/group/${params.row.GroupID}`)}
          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
        >
          {params.row.GroupName}
        </Button>
      ),
    },
    {
      field: 'GroupType',
      headerName: 'סוג הקבוצה',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        // Use the group schemas to get the label
        return getGroupTypeLabel(params.row.GroupType);
      },
    },
    {
      field: 'Description',
      headerName: 'תיאור',
      flex: 2,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'Actions',
      headerName: 'פעולות',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Button
          color="error"
          variant="outlined"
          onClick={() => confirmDeleteGroup(params.row)}
        >
          הסר
        </Button>
      ),
    },
  ];

  if (!includeGroups) {
    return null; // Don't render if groups are not included in config
  }

  return (
    // 3) Wrap the return with ThemeProvider for bigger base fonts
    <ThemeProvider theme={theme}>
      <div dir="rtl">
        <Container maxWidth={false} sx={{ width: '100%', px: 2 }}>
          {/* Increase the main title font size */}
          <Typography variant="h3" sx={{ mb: 3}}>
            ניהול קבוצות
          </Typography>

          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  רשימת קבוצות
                </Typography>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 1.5,
              }}
            />
            <CardContent>       
              <ResponsiveToolbar>
                <TextField
                  label="חפש קבוצה"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: { xs: '100%', md: 300 } }}
                />

                <TextField
                  select
                  label="סינון לפי סוג קבוצה"
                  size="small"
                  value={groupTypeFilter}
                  onChange={(e) => setGroupTypeFilter(e.target.value)}
                  sx={{ width: { xs: '100%', md: 200 } }}
                >
                  <MenuItem value="All">הכל</MenuItem>
                  {/* Dynamically render menu items based on allowed group types */}
                  {allowedGroupTypes.map(groupType => (
                    <MenuItem key={groupType} value={groupType}>
                      {getGroupTypeLabel(groupType)}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  יצירת קבוצה חדשה
                </Button>
              </ResponsiveToolbar>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <ResponsiveDataView
                  GridComponent={DataGrid}
                  rows={filteredGroups}
                  columns={columns}
                  cardRenderer={(row) => (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {row.GroupName}
                      </Typography>
                      {row.GroupType && (
                        <Typography variant="body2" color="text.secondary">
                          סוג: {row.GroupType}
                        </Typography>
                      )}
                      {row.Description && (
                        <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.Description}
                        </Typography>
                      )}
                    </Box>
                  )}
                  onRowClick={(row) => navigate(`/group/${row.GroupID}`)}
                  getRowId={(row) => row.GroupID}
                  emptyMessage="אין קבוצות"
                  localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                  autoHeight
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 25, page: 0 },
                    },
                  }}
                  disableSelectionOnClick
                  sx={{
                    '.MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                      whiteSpace: 'normal !important',
                      lineHeight: '1.2',
                      overflow: 'visible',
                    },
                    '.MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      fontSize: '1.1rem',
                    },
                  }}
                />
              )}
            </CardContent>
          </Card>

          {/* Create Group Dialog - Updated to use config */}
          <MobileDialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ fontSize: '1.8rem' }}>יצירת קבוצה חדשה</DialogTitle>
            <DialogContent sx={{ fontSize: '1.5rem' }}>
              <TextField
                label="שם הקבוצה"
                fullWidth
                margin="dense"
                value={newGroup.group_name}
                onChange={(e) =>
                  setNewGroup({ ...newGroup, group_name: e.target.value })
                }
                InputProps={{ style: { fontSize: '1.5rem' } }}
                InputLabelProps={{ style: { fontSize: '1.5rem' } }}
              />
              <TextField
                label="תיאור הקבוצה"
                fullWidth
                margin="dense"
                value={newGroup.description}
                onChange={(e) =>
                  setNewGroup({ ...newGroup, description: e.target.value })
                }
                InputProps={{ style: { fontSize: '1.5rem' } }}
                InputLabelProps={{ style: { fontSize: '1.5rem' } }}
              />
              <TextField
                select
                label="סוג הקבוצה"
                fullWidth
                margin="dense"
                value={newGroup.group_type}
                onChange={(e) =>
                  setNewGroup({ ...newGroup, group_type: e.target.value })
                }
                InputProps={{ style: { fontSize: '1.5rem' } }}
                InputLabelProps={{ style: { fontSize: '1.5rem' } }}
              >
                {/* Dynamically render menu items based on allowed group types */}
                {allowedGroupTypes.map(groupType => (
                  <MenuItem key={groupType} value={groupType} style={{ fontSize: '1.5rem' }}>
                    {getGroupTypeLabel(groupType)}
                  </MenuItem>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCreateDialogOpen(false)}
                sx={{ color: 'red', fontSize: '1.5rem' }}
              >
                ביטול
              </Button>
              <Button
                onClick={handleCreateGroup}
                color="primary"
                sx={{ fontSize: '1.5rem' }}
              >
                יצירה
              </Button>
            </DialogActions>
          </MobileDialog>

          {/* Confirm Delete Dialog with Password Protection */}
          <MobileDialog
            open={deleteDialogOpen}
            onClose={() => {
              setDeleteDialogOpen(false);
              setDeleteDialogStep('confirm');
              setDeletePassword('');
              setDeletePasswordVisible(false);
              setDeleteFinalError('');
              setDeleteFinalSuccess('');
            }}
            fullWidth
            maxWidth="sm"
          >
            {deleteDialogStep === 'confirm' && (
              <>
                <DialogTitle>אישור מחיקה</DialogTitle>
                <DialogContent>
                  האם אתה בטוח שברצונך למחוק את הקבוצה "{groupToDelete?.GroupName}"?
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    setDeleteDialogOpen(false);
                    setDeleteDialogStep('confirm');
                    setDeletePassword('');
                    setDeletePasswordVisible(false);
                    setDeleteFinalError('');
                    setDeleteFinalSuccess('');
                  }}>ביטול</Button>
                  <Button color="primary" onClick={() => setDeleteDialogStep('password')}>המשך</Button>
                </DialogActions>
              </>
            )}
            {deleteDialogStep === 'password' && (
              <>
                <DialogTitle>הזן סיסמה לאישור מחיקה</DialogTitle>
                <DialogContent>
                  {deleteFinalSuccess ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {deleteFinalSuccess}
                    </Alert>
                  ) : (
                    <>
                      {deleteFinalError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {deleteFinalError}
                        </Alert>
                      )}
                      <TextField
                        label="סיסמה"
                        type={deletePasswordVisible ? 'text' : 'password'}
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        fullWidth
                        sx={{ mb: 2,  direction: 'ltr' }}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={() => setDeletePasswordVisible(!deletePasswordVisible)}
                              edge="end"
                            >
                              {deletePasswordVisible ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          ),
                        }}
                      />
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  {deleteFinalSuccess ? (
                    <Button onClick={() => {
                      setDeleteDialogOpen(false);
                      setDeleteDialogStep('confirm');
                      setDeletePassword('');
                      setDeletePasswordVisible(false);
                      setDeleteFinalSuccess('');
                    }}>
                      סגור
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => {
                          setDeleteDialogStep('confirm');
                          setDeleteFinalError('');
                        }}
                        disabled={deleteFinalLoading}
                      >
                        חזרה
                      </Button>
                      <Button color="error" variant="contained" onClick={handleDeleteGroup} disabled={deleteFinalLoading}>
                        {deleteFinalLoading ? (
                          <>
                            <CircularProgress size={24} sx={{ mr: 1, color: 'inherit' }} />
                            מעדכן...
                          </>
                        ) : (
                          'אישור מחיקה'
                        )}
                      </Button>
                    </>
                  )}
                </DialogActions>
              </>
            )}
          </MobileDialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </div>
    </ThemeProvider>
  );
};

export default GroupsManagementPage;